import { Badge, Box, Button, Icon, IconButton, Stack, useColorModeValue, useDisclosure } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { HiPencil, HiTrash } from 'react-icons/hi';
import { HiOutlinePlusCircle } from 'react-icons/hi2';

import { AlertModal } from './AlertModal/AlertModal';

export interface SimpleFieldArrayForm {
  prefix: string;
  isFirst: boolean;
  isLast: boolean;
  hasMultiple: boolean;
}

interface SimpleFieldArrayProps {
  addLabelOne?: string;
  addLabel?: string;
  name: string;
  form: (props: SimpleFieldArrayForm) => React.ReactElement;
  min?: number;
  onRemove?: (index: number) => void;
  accordionTitle?: (field: any) => React.ReactElement;
  canAdd?: boolean;
  canRemove?: boolean;
  ignoreStyles?: boolean;
  deleteConfirmationDetails?: {
    title: string;
    message: string;
    btnCancel: string;
    btnDelete: string;
  };
  atLeastOneFieldOpen?: boolean;
  defaultValue?: any;
}

export const SimpleFieldArray = ({
  name,
  addLabel,
  addLabelOne,
  form,
  min = 0,
  onRemove,
  accordionTitle,
  ignoreStyles = false,
  canAdd = true,
  canRemove = true,
  deleteConfirmationDetails,
  atLeastOneFieldOpen,
  defaultValue = {},
}: SimpleFieldArrayProps) => {
  const { control, formState, getValues, watch } = useFormContext();
  const { append, fields, remove } = useFieldArray({ control, name });
  // Intentionally redraw on value change
  watch(name);

  const { isOpen: isDeleteOpen, onClose: closeDelete, onOpen: openDelete } = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(-1);

  const errors = formState?.errors?.[name];

  const showRemove = canRemove && fields.length > min;
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const setActive = (index: number) => () => setActiveIndex(index);
  const hasMultiple = fields.length > 1;

  const asAccordion = !!accordionTitle;

  const handleAddItem = useCallback(() => {
    append(defaultValue);
    const nextIndex = fields.length;
    setActiveIndex(nextIndex);
  }, [append, defaultValue, fields.length]);

  const handleRemove = (index: number) => () => {
    closeDelete?.();
    onRemove?.(index);

    remove(index);
    const isFirstItem = index === 0;
    const nextActive = isFirstItem ? 0 : index - 1;
    setActiveIndex(nextActive);
  };

  useEffect(() => {
    if (atLeastOneFieldOpen && !fields.length) handleAddItem();
  }, [atLeastOneFieldOpen, handleAddItem, fields]);

  if (atLeastOneFieldOpen && activeIndex === -1 && fields.length) {
    setActiveIndex(fields.length - 1);
  }

  return (
    <Stack direction="column" spacing={ignoreStyles ? 0 : 8} width="full">
      <Show if={!!deleteConfirmationDetails}>
        <AlertModal
          isOpen={isDeleteOpen}
          onClose={closeDelete}
          title={deleteConfirmationDetails?.title}
          message={deleteConfirmationDetails?.message}
          btnCancel={deleteConfirmationDetails?.btnCancel}
          btnDelete={deleteConfirmationDetails?.btnDelete}
          handleConfirm={() => {
            handleRemove(deleteTarget)();
          }}
        />
      </Show>
      {fields.map((field, index) => {
        const isFirst = index === 0;
        const isLast = index === fields.length - 1;
        const isActive = activeIndex === index;
        // const hasError = errors?.[index];
        // TODO(jeremy): this may break
        const hasError = (errors as any)?.[index];

        const wrapDelete = () => {
          setDeleteTarget(index);
          openDelete();
        };

        // "field" actually has a good chance of not being the live value. This grabs the latest value for the title
        const liveValue = getValues()?.[name]?.[index] ?? field;

        return (
          <Stack
            direction="row"
            key={field.id}
            pt={ignoreStyles ? 0 : isFirst ? 0 : 8}
            borderTop={ignoreStyles ? 0 : isFirst ? '' : '1px solid'}
            alignItems="center"
            borderTopColor={ignoreStyles ? undefined : borderColor}
          >
            <Stack width="full" pl={ignoreStyles ? 0 : 2}>
              <Show if={asAccordion}>
                <Box w="full" alignSelf="center">
                  {accordionTitle?.(liveValue)}
                  <Show if={hasError}>
                    <Badge colorScheme="red">Oops, this has some errors</Badge>
                  </Show>
                </Box>
              </Show>
              <Show if={!asAccordion || isActive}>
                <Stack spacing={ignoreStyles ? 0 : 8} direction="column" width="100%">
                  {form({
                    prefix: `${name}.${index}`,
                    isFirst,
                    isLast,
                    hasMultiple,
                  })}
                </Stack>
              </Show>
            </Stack>
            <Show if={asAccordion}>
              <IconButton
                size="sm"
                aria-label="Edit Row"
                variant="ghost"
                icon={<Icon as={HiPencil} fontSize={16} />}
                onClick={index === activeIndex ? setActive(-1) : setActive(index)}
              />
            </Show>
            <Show if={showRemove}>
              <IconButton
                size="sm"
                aria-label="Remove Row"
                colorScheme="red"
                variant="ghost"
                icon={<Icon as={HiTrash} fontSize={16} />}
                onClick={deleteConfirmationDetails ? wrapDelete : handleRemove(index)}
              />
            </Show>
          </Stack>
        );
      })}

      <Show if={canAdd}>
        <Button
          onClick={handleAddItem}
          leftIcon={<Icon as={HiOutlinePlusCircle} />}
          variant="outline"
          alignSelf="flex-start"
        >
          {fields.length < 1 ? addLabelOne || addLabel : addLabel}
        </Button>
      </Show>
    </Stack>
  );
};
